import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { revenueDetails } from '../../actions/adminRevenueAction';
import { totalUserDetails } from '../../actions/adminUserActions';

interface Card {
  label: string;
  value: string;
  change: number | null;
  changeLabel: string;
  icon: string;
  accent: string;
}

const StatCard = ({ label, value, change, changeLabel, icon, accent }: Card) => {
  const isUp = change !== null && change > 0;
  const isDown = change !== null && change < 0;

  return (
    <div className="stat-card">
      <div className="stat-card-accent" style={{ background: accent }} />
      <div className="stat-card-body">
        <div className="stat-card-icon" style={{ color: accent }}>
          <i className={icon} />
        </div>
        <p className="stat-card-label">{label}</p>
        <p className="stat-card-value">{value}</p>
        {change !== null && (
          <p className={`stat-card-change ${isUp ? 'up' : isDown ? 'down' : 'flat'}`}>
            <i className={`fas fa-arrow-${isUp ? 'up' : isDown ? 'down' : 'right'} mr-1`} />
            {Math.abs(change)}% <span>{changeLabel}</span>
          </p>
        )}
      </div>
    </div>
  );
};

const StatCards = ({ adminRevenueCalc, adminUserCalc, revenueDetails, totalUserDetails }: any) => {
  useEffect(() => {
    revenueDetails();
    totalUserDetails();
  }, []);
  const calcPercent = (today: number, yesterday: number) => {
    if (yesterday === 0) return today > 0 ? 100 : 0;
    return Math.floor(((today - yesterday) / yesterday) * 100);
  };

  const dailyRevenue = adminRevenueCalc.dailyRevenue / 100;
  const yesterdayRevenue = adminRevenueCalc.yesterdayRevenue / 100;
  const ordersToday = adminRevenueCalc.totalOrdersToday;
  const ordersYesterday = adminRevenueCalc.totalOrdersYesterday;
  const avgToday = ordersToday > 0 ? dailyRevenue / ordersToday : 0;
  const avgYesterday = ordersYesterday > 0 ? yesterdayRevenue / ordersYesterday : 0;

  const userIncrease = calcPercent(adminUserCalc.weeklyCount, adminUserCalc.previousWeekCount);

  const cards: Card[] = [
    {
      label: 'Revenue Today',
      value: `$${dailyRevenue.toFixed(2)}`,
      change: calcPercent(Math.round(dailyRevenue), Math.round(yesterdayRevenue)),
      changeLabel: 'vs yesterday',
      icon: 'fas fa-dollar-sign',
      accent: '#ec174f',
    },
    {
      label: 'Orders Today',
      value: String(ordersToday),
      change: calcPercent(ordersToday, ordersYesterday),
      changeLabel: 'vs yesterday',
      icon: 'fas fa-shopping-bag',
      accent: '#0082bf',
    },
    {
      label: 'Avg Order Value',
      value: `$${avgToday.toFixed(2)}`,
      change: calcPercent(Math.round(avgToday), Math.round(avgYesterday)),
      changeLabel: 'vs yesterday',
      icon: 'fas fa-chart-line',
      accent: '#7c3aed',
    },
    {
      label: 'Active Users',
      value: String(adminUserCalc.totalUserCount),
      change: userIncrease,
      changeLabel: 'vs last week',
      icon: 'fas fa-users',
      accent: '#059669',
    },
  ];

  return (
    <div className="stat-cards-grid">
      {cards.map((card) => (
        <StatCard key={card.label} {...card} />
      ))}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  adminRevenueCalc: state.adminRevenueCalc,
  adminUserCalc: state.adminUserCalc,
});

export default connect(mapStateToProps, { revenueDetails, totalUserDetails })(StatCards);
